import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core'
import { Store } from '@ngrx/store'
import { GlobalConstants } from 'src/app/constants/global-constants'
import { load_dashboard_state } from 'src/app/store/action/dashboard.action'
import { AppState } from 'src/app/store/state/app.state'
import { Observable, Subject, Subscription } from 'rxjs'
import { DashboardState, initialUserDashboardState } from '../../store/state/dashboard.state'
import { getUserDashboardState } from '../../store/selector/userDashboard.selector'
import { CalendarEvent, CalendarView } from 'angular-calendar'
import { EventColor } from 'calendar-utils'
import * as moment from 'moment'
import { DocumentAPIModel } from '../../models/api.model'
import { DocumentService } from '../../services/common/document.service'
import { ServiceUrlConstants } from 'src/app/constants/service-url-constants'
import { DomSanitizer } from '@angular/platform-browser'
import { trigger, transition, style, animate } from '@angular/animations'

const colors: Record<string, EventColor> = {
  red: {
    primary: '#ad2121',
    secondary: '#FAE3E3'
  },
  blue: {
    primary: '#1e90ff',
    secondary: '#D1E8FF'
  },
  yellow: {
    primary: '#e3bc08',
    secondary: '#FDF1BA'
  }
}

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  animations: [
    trigger('slideIn', [
      transition(':enter', [
        style({ transform: 'translateX(100%)', opacity: 0 }),
        animate('1000ms ease-in', style({ transform: 'translateX(0)', opacity: 1 }))
      ])
    ])
  ]
})
export class DashboardComponent implements OnInit {
  readonly VIEW_INFO = `/${GlobalConstants.ROUTING.PROFILE}`
  readonly ACTIVITY_DASHBOARD = `/${GlobalConstants.ROUTING.ACTIVITY}/${GlobalConstants.ROUTING.DASHBOARD}`
  readonly QUERY_DASHBOARD = `/${GlobalConstants.ROUTING.QUERY}/${GlobalConstants.ROUTING.DASHBOARD}`

  userDashboardDetails$: Subscription
  calendarEvents = []

  @ViewChild('modalContent', { static: true }) modalContent: TemplateRef<any>
  view: CalendarView = CalendarView.Month

  CalendarView = CalendarView

  viewDate = new Date()

  refresh = new Subject<void>()

  events: CalendarEvent[] = []

  activeDayIsOpen: boolean = false

  userDashboardDetails: DashboardState

  visibleAnnouncements = []
  currentIndex: number = 0
  interval = null
  isPaused: boolean = false

  dashboard = {
    displayAnnouncementModel: {
      show: false,
      content: undefined,
      title: '',
      isAttachmentDocumentAvailable: false,
      attachments: [],
      companyId: ''
    },
    displayCalendarModel: {
      show: false,
      content: [],
      title: '',
      date: ''
    }
  }

  constructor(
    private appState: Store<AppState>,
    private documentService: DocumentService,
    protected _sanitizer: DomSanitizer
  ) {
    this.userDashboardDetails$ = this.appState.select(getUserDashboardState).subscribe((userDashboardDetails) => {
      if (userDashboardDetails != initialUserDashboardState) {
        this.userDashboardDetails$?.unsubscribe()
        this.userDashboardDetails = userDashboardDetails
        this.startCarousel()
        this.getCalendarEvent()
      }
    })
  }

  ngOnInit() {
    this.appState.dispatch(load_dashboard_state())
  }

  getCalendarEvent() {
    this.calendarEvents = this.userDashboardDetails.calendarEvents
    if (this.calendarEvents?.length > 0) {
      this.buildEventDisplayIconOnCalendar(this.calendarEvents)
    }
  }

  buildEventDisplayIconOnCalendar(calendarEvents) {
    const calendarEvent = []
    const uniqueDateAndreferenceType = []
    calendarEvents.forEach((obj) => {
      const date = moment(obj.dueDatetime).format('MM/DD/YYYY')
      const referenceType = obj.referenceType
      const isDuplicate = uniqueDateAndreferenceType.some(
        (item) => moment(item.dueDatetime).format('MM/DD/YYYY') === date && item.referenceType === referenceType
      )
      if (!isDuplicate) {
        uniqueDateAndreferenceType.push(obj)
      }
    })
    uniqueDateAndreferenceType.forEach((calEvent) => {
      calendarEvent.push({
        id: calEvent.displayId,
        start: new Date(calEvent.dueDatetime),
        color:
          calEvent.referenceType == 'ACTIVITY'
            ? { ...colors['blue'] }
            : calEvent.referenceType == 'QUERIES' || !calEvent.referenceType
            ? { ...colors['yellow'] }
            : { ...colors['red'] }
      })
    })
    this.events = calendarEvent
    this.refresh.next()
  }

  displayNotificationDetails(data) {
    const displayAnnouncementModel = {
      show: true,
      content: this._sanitizer.bypassSecurityTrustHtml(data.description),
      title: data.heading,
      isAttachmentDocumentAvailable: data.documents.length > 0,
      attachments: data.documents,
      companyId: data.companyEntityId
    }

    this.dashboard.displayAnnouncementModel = displayAnnouncementModel
  }

  closeAnnouncementPopup() {
    this.dashboard.displayAnnouncementModel = {
      show: false,
      attachments: [],
      content: undefined,
      isAttachmentDocumentAvailable: false,
      title: '',
      companyId: ''
    }
  }

  closeCalendarPopup() {
    this.dashboard.displayCalendarModel = {
      show: false,
      content: [],
      title: '',
      date: ''
    }
  }

  closeOpenMonthViewDay() {
    this.activeDayIsOpen = false
  }

  openEventDetailsPopup(ev) {
    const calendarDisplayData = this.calendarEvents.find((res) => {
      return res.displayId == ev.event.id
    })

    const displayData = this.calendarEvents.filter((item) => {
      return (
        moment(item.dueDatetime).format('MM/DD/YYYY') ===
          moment(calendarDisplayData.dueDatetime).format('MM/DD/YYYY') &&
        item.referenceType === calendarDisplayData.referenceType
      )
    })
    const displayCalendarModel = {
      show: true,
      content: displayData,
      title: calendarDisplayData.referenceType,
      date: calendarDisplayData.dueDatetime
    }

    this.dashboard.displayCalendarModel = displayCalendarModel
  }

  downloadDocument = (document: DocumentAPIModel, companyId: string) => {
    const endPoint = `${ServiceUrlConstants.API_DOWNLOAD_DELETE_DOCUMENT}${document.id}`
    this.documentService.downloadDocument(endPoint)
  }

  startCarousel() {
    this.updateVisibleAnnouncements()
    this.interval = setInterval(() => {
      this.nextAnnouncement()
    }, 5000)
  }

  updateVisibleAnnouncements() {
    this.visibleAnnouncements = [this.userDashboardDetails?.announcements[this.currentIndex]]
  }

  nextAnnouncement() {
    this.currentIndex = (this.currentIndex + 1) % this.userDashboardDetails?.announcements?.length
    this.updateVisibleAnnouncements()
  }

  prevAnnouncement() {
    this.currentIndex =
      (this.currentIndex - 1 + this.userDashboardDetails?.announcements?.length) %
      this.userDashboardDetails?.announcements?.length
    this.updateVisibleAnnouncements()
  }

  pauseCarousel() {
    clearInterval(this.interval)
  }

  togglePausePlay() {
    if (this.isPaused) {
      this.startCarousel()
    } else {
      clearInterval(this.interval)
    }
    this.isPaused = !this.isPaused
  }
}
